/* eslint-disable */
import { gsap, Expo, CSSPlugin } from 'gsap';
import { ScrollToPlugin } from 'gsap/all';

gsap.registerPlugin(CSSPlugin);
gsap.registerPlugin(ScrollToPlugin);

/**
 * Opens an element
 *
 * @example
 * import {open} from '../helpers';
 *
 * open({ element: this.el.querySelector('.element)});
 *
 * @param {Object} element - HTML element you want to target for animation
 * @param {Number} duration - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */
export const open = ({
  element,
  duration = 0.4,
  ease = 'easeOut',
  onComplete
}) => {
  gsap.set(element, { height: 'auto' });
  gsap.from(element, {
    height: 0,
    ease: Expo[ease],
    duration,
    onComplete: () => {
      element.style.height = null;
      onComplete && onComplete();
    }
  });
};

/**
 * Closes an element
 *
 * @example
 * import {close} from '../helpers';
 *
 * close({ element: this.el.querySelector('.element)});
 *
 * @param {Object} element - HTML element you want to target for animation
 * @param {Number} duration - Speed of the animation
 * @param {String} ease - Ease of the animation
 * @param {Function} onComplete - On complete function
 */
export const close = ({
  element,
  duration = 0.4,
  ease = 'easeOut',
  onComplete
}) => {
  gsap.set(element, { height: element.offsetHeight });
  gsap.to(element, {
    height: 0,
    ease: Expo[ease],
    duration,
    onComplete: () => {
      element.style.height = null;
      onComplete && onComplete();
    }
  });
};

// /**
// * Scrolls to a certain part of the page with GSAP
// *
// * @param {Number} y - The new y position
// * @param {Number} speed - The spped in which the screen scrolls
// * @param {Function} onComplete - On complete function
// */
export const scrollTo = (y, duration = 1.5, onComplete = () => {}) => {
  gsap.to(window, {
    scrollTo: { y },
    duration,
    ease: Expo.easeOut,
    onComplete
  });
};

// /**
// * Checks if an element is focusable
// *
// * @param {Object} el - HTML element you want to check if it's focusable
// */
export const isFocusable = el => {
  if (
    el.tabIndex > 0 ||
    (el.tabIndex === 0 && el.getAttribute('tabIndex') !== null)
  ) {
    return true;
  }

  if (el.disabled) {
    return false;
  }

  /* eslint-disable indent */
  switch (el.nodeName) {
    case 'A':
      return !!el.href && el.rel !== 'ignore';
    case 'INPUT':
      return el.type !== 'hidden' && el.type !== 'file';
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true;
    default:
      return false;
  }
  /* eslint-enable indent */
};

// /**
// * Checks browser is running on a native mobile device
// */
export const isDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (
    /windows phone/i.test(userAgent) ||
    /android/i.test(userAgent) ||
    (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream)
  ) {
    return true;
  }

  return false;
};

// /**
// * Checks if browser is IE 11
// */
export const isIE11 = () => {
  if (!!window.MSInputMethodContext && !!document.documentMode) {
    return true;
  }

  return false;
};

// /**
// * Gets URL params
// */
export const getUrlParams = (search = window.location.search) => {
  const hashes = search.slice(search.indexOf('?') + 1).split('&');
  const params = {};
  hashes.map(hash => {
    const [key, val] = hash.split('=');
    if (key) {
      params[key] = val ? decodeURIComponent(val) : val;
    }
  });
  return params;
};

// /**
// * Debounce function
// * Delays the processing of the event
// */
export const debounce = (callback, wait) => {
  let timerId;
  return (...args) => {
    clearTimeout(timerId);
    timerId = setTimeout(() => {
      callback(...args);
    }, wait);
  };
};

// /**
// * Key code list object
// */
export const keyCode = {
  TAB: 9,
  RETURN: 13,
  ESC: 27,
  SPACE: 32,
  PAGEUP: 33,
  PAGEDOWN: 34,
  END: 35,
  HOME: 36,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40
};

export const getAbsoluteCoords = el => {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + window.scrollX,
    top: rect.top + window.scrollY
  };
};

const setCookie = (cname, cvalue, exdays) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${cname}=${cvalue};${expires};path=/`;
};

const getCookie = cname => {
  const name = `${cname}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return undefined;
};

function digit2string(i) {
  // 0-9 -> 0-9; 10-35 > a-z; 36-61 -> A-Z
  return String.fromCharCode(i < 10 ? i + 48 : i < 36 ? i + 87 : i + 29);
}

const generateUUID = domainId => {
  const uuidx = 'xx-xx-4x-1p-';
  let uuid = uuidx.replace(/[x]/g, () =>
    digit2string((Math.random() * 36) | 0)
  );

  for (let i = 0; i < 5; i++) {
    uuid += ('0000' + ((Math.random() * 1679615) | 0).toString(36)).slice(-4);
  }

  let value = domainId;
  value += '-' + uuid + '-' + Date.now();
  return value;
};

export const getIp = async () => {
  let currentIp;

  try {
    currentIp = getCookie('currentIp');
    if (currentIp) {
      return currentIp;
    }
    const ip = await fetch('https://api.ipify.org?format=json');
    const ipJson = await ip.json();
    currentIp = ipJson.ip;
    setCookie('currentIp', currentIp, 1);
    return currentIp;
  } catch (error) {
    console.error('Error:', error);
  }

  return currentIp;
};

export const getDomainId = () => {
  let uuid = getCookie('__ruid');
  if (uuid) {
    return uuid;
  }
  uuid = generateUUID('230899993');
  setCookie('__ruid', uuid, 1);
  return uuid;
};

export const context = async (locale_country, locale_language) => ({
  page: {
    uri: window.location.pathname
  },
  user: {
    cuid: getDomainId()
  },
  geo: {
    ip: await getIp()
  },
  browser: {
    user_agent: navigator.userAgent
  },
  locale: {
    country: locale_country,
    language: locale_language
  }
});

export const entityPageView = async (rfk_id, entities, locale_country, locale_language) => {
  return {
    name: 'entity_page',
    action: 'view',
    uuid: getDomainId(),
    client_time_ms: Date.now(),
    value: {
      context: await context(locale_country, locale_language),
      entities,
      rfk_id
    }
  };
};

export const publishEvent = async data => {
  return await fetch(
    'https://discover.sitecorecloud.io/event/65028203-230899993/v4/publish',
    {
      method: 'POST',
      headers: {
        Authorization: '01-37cb036f-82dae32b091f9247969fd976f6219d734d9e73be',
        'Content-type': 'application/json; charset=UTF-8'
      },
      body: JSON.stringify(data)
    }
  );
};
